import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../contexts/AuthContext';
import { sendVerificationCode } from '../lib/api';

type AuthMode = 'signin' | 'register' | 'reset';

export function LoginPage() {
  const { user, loading, login, register, resetPassword } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [aiAccepted, setAiAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  if (!loading && user) return <Navigate to="/workspace" replace />;

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setPassword('');
    setConfirmPassword('');
    setCode('');
    setCodeSent(false);
    setLegalAccepted(false);
    setAiAccepted(false);
    setError('');
    setNotice('');
  };

  const sendCode = async () => {
    setError('');
    setNotice('');
    setSendingCode(true);
    try {
      await sendVerificationCode(email);
      setCodeSent(true);
      setNotice('A six-digit code has been sent. It is valid for 15 minutes.');
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'The verification code could not be sent.');
    } finally {
      setSendingCode(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (mode !== 'signin' && !codeSent) {
      setError('Request a verification code first.');
      return;
    }
    if (mode !== 'signin' && password !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    if (mode === 'register' && (!legalAccepted || !aiAccepted)) {
      setError('Please review and accept the account and AI processing terms.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signin') await login(email, password);
      if (mode === 'register') await register(email, code, password);
      if (mode === 'reset') await resetPassword(email, code, password);
      const target = (location.state as { from?: string } | null)?.from || '/workspace';
      navigate(target, { replace: true });
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Sign in could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <BrandMark />
        <div className="auth-card__heading">
          <p className="eyebrow">Private reflection workspace</p>
          <h1>{mode === 'signin' ? 'Welcome back' : mode === 'register' ? 'Create your account' : 'Reset your password'}</h1>
          <p>
            {mode === 'signin'
              ? 'Sign in to continue your conversations and use your question balance.'
              : mode === 'register'
                ? 'Verify your email to keep conversations and purchased questions private.'
                : 'Verify your email and choose a new password.'}
          </p>
        </div>
        <form onSubmit={submit}>
          <label className="field-label" htmlFor="login-email">Email address</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (codeSent) {
                setCodeSent(false);
                setCode('');
                setNotice('');
              }
            }}
            required
          />
          {mode !== 'signin' ? (
            <>
              <button
                className="button button--outline button--full auth-code-button"
                type="button"
                disabled={sendingCode || !email.trim()}
                onClick={sendCode}
              >
                {sendingCode ? 'Sending…' : codeSent ? 'Send a new code' : 'Send verification code'}
              </button>
              <label className="field-label" htmlFor="login-code">Verification code</label>
              <input
                id="login-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </>
          ) : null}
          <label className="field-label" htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          {mode !== 'signin' ? (
            <>
              <label className="field-label" htmlFor="login-confirm-password">Confirm password</label>
              <input
                id="login-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </>
          ) : null}
          {mode === 'register' ? (
            <div className="auth-consents">
              <label>
                <input type="checkbox" checked={legalAccepted} onChange={(event) => setLegalAccepted(event.target.checked)} />
                <span>I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</span>
              </label>
              <label>
                <input type="checkbox" checked={aiAccepted} onChange={(event) => setAiAccepted(event.target.checked)} />
                <span>I agree that my questions and relevant conversation context may be processed by contracted AI providers to generate responses.</span>
              </label>
            </div>
          ) : null}
          {notice ? <p className="form-notice" role="status">{notice}</p> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button button--warm button--full" type="submit" disabled={busy || loading}>
            {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Reset password'}
          </button>
        </form>
        <div className="auth-card__actions">
          {mode === 'signin' ? (
            <>
              <button type="button" onClick={() => switchMode('register')}>Create an account</button>
              <button type="button" onClick={() => switchMode('reset')}>Forgot password?</button>
            </>
          ) : (
            <button type="button" onClick={() => switchMode('signin')}>Back to sign in</button>
          )}
        </div>
        <p className="auth-card__note">Your account keeps purchased questions and conversation history available across devices.</p>
      </section>
    </main>
  );
}
